import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Archive, CalendarPlus, Download, Forward, Inbox, Loader2, MailOpen, Reply, Trash2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import BookingReviewDialog from "@/components/mailbox/BookingReviewDialog";

const API = process.env.REACT_APP_BACKEND_URL;

function GuestCard({ g }) {
  const { t } = useLang();
  if (!g) return null;
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-sm" data-testid="mail-guest-card">
      <p className="font-semibold flex items-center gap-1.5 text-emerald-800"><UserCheck className="h-4 w-4" />{t("mb_known_guest")}: {g.guest_name}</p>
      <p className="text-xs text-muted-foreground">{[g.country, g.phone].filter(Boolean).join(" · ")}</p>
      {g.reservations?.length > 0 && (
        <ul className="mt-2 space-y-0.5 text-xs">
          {g.reservations.map((r) => (
            <li key={r.id} className="flex items-center gap-2">
              <span className="font-mono">{r.arrival} → {r.departure}</span>
              <span className="text-muted-foreground">{r.pitch_label}</span>
              {!r.definitive && <span className="rounded-full bg-amber-100 text-amber-800 px-1.5 text-[10px] font-semibold">{t("mb_provisional")}</span>}
            </li>
          ))}
        </ul>
      )}
      <Link to={`/gasten?q=${encodeURIComponent(g.guest_name)}`} className="mt-2 inline-block text-xs font-semibold text-primary hover:underline" data-testid="mail-guest-link">{t("mb_open_guest")}</Link>
    </div>
  );
}

export default function MessageView({ uid, folder, onChanged, onReply }) {
  const { t } = useLang();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [review, setReview] = useState(null); // voorgestelde reservatiegegevens uit de mail
  const navigate = useNavigate();

  // Vanuit de mailbox: AI leest de mail (eender welke taal) en stelt de reservatiegegevens voor.
  // De receptie controleert en vult aan in een tussenscherm vóór het reservatieformulier.
  const toReservation = async () => {
    setExtracting(true);
    try {
      const { data: p } = await api.post(`/mailbox/messages/${uid}/extract-booking`, null, { params: { folder } });
      setReview(p);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setExtracting(false);
    }
  };

  // "Reservatie aanmaken": de (aangevulde) gegevens uit het tussenscherm doorgeven aan het formulier.
  const createFromReview = (payload) => {
    sessionStorage.setItem("campad.mail_prefill", JSON.stringify(payload));
    setReview(null);
    navigate("/reservaties?from_mail=1");
  };

  // "Vraag meer info": antwoordvenster openen met een kant-en-klare tekst in de taal van de gast.
  const askInfo = (body) => {
    setReview(null);
    onReply(m, "reply", { initialBody: body });
  };

  useEffect(() => {
    setM(null);
    api.get(`/mailbox/messages/${uid}`, { params: { folder } }).then(({ data }) => setM(data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [uid, folder]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const move = (to) => act(() => api.post(`/mailbox/messages/${uid}/move`, { folder, to }), t("mb_moved"));
  const unread = () => act(() => api.post(`/mailbox/messages/${uid}/flags`, { folder, seen: false }));

  if (!m) return <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{t("loading")}</div>;

  const doc = m.html || `<pre style="white-space:pre-wrap;font-family:system-ui,sans-serif;font-size:14px;margin:0">${(m.text || "").replace(/</g, "&lt;")}</pre>`;
  return (
    <div className="p-5 space-y-4" data-testid="mail-view">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="font-display font-bold text-lg leading-tight break-words" data-testid="mail-view-subject">{m.subject}</h2>
          <p className="text-sm mt-1"><span className="font-semibold">{m.from.name || m.from.email}</span> <span className="text-muted-foreground">&lt;{m.from.email}&gt;</span></p>
          <p className="text-xs text-muted-foreground">{t("mb_to")}: {m.to.map((x) => x.email).join(", ")}{m.cc?.length ? ` · CC: ${m.cc.map((x) => x.email).join(", ")}` : ""}</p>
          <p className="text-xs text-muted-foreground font-mono">{m.date ? new Date(m.date).toLocaleString() : ""}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" onClick={() => onReply(m, "reply")} data-testid="mail-reply"><Reply className="h-4 w-4 mr-1" />{t("mb_reply")}</Button>
          {folder !== "sent" && <Button size="sm" variant="outline" onClick={toReservation} disabled={extracting} className="text-emerald-700 border-emerald-300" data-testid="mail-to-reservation">{extracting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CalendarPlus className="h-4 w-4 mr-1" />}{t("mb_make_reservation")}</Button>}
          <Button size="sm" variant="outline" onClick={() => onReply(m, "forward")} data-testid="mail-forward"><Forward className="h-4 w-4 mr-1" />{t("mb_forward")}</Button>
          {folder === "inbox" && <Button size="sm" variant="outline" disabled={busy} onClick={unread} title={t("mb_mark_unread")} data-testid="mail-unread"><MailOpen className="h-4 w-4" /></Button>}
          {folder !== "archive" && <Button size="sm" variant="outline" disabled={busy} onClick={() => move("archive")} title={t("mb_archive")} data-testid="mail-archive"><Archive className="h-4 w-4" /></Button>}
          {folder !== "inbox" && <Button size="sm" variant="outline" disabled={busy} onClick={() => move("inbox")} title={t("mb_inbox")} data-testid="mail-to-inbox"><Inbox className="h-4 w-4" /></Button>}
          {folder !== "trash" && <Button size="sm" variant="outline" disabled={busy} onClick={() => move("trash")} title={t("mb_trash")} className="text-rose-600" data-testid="mail-trash"><Trash2 className="h-4 w-4" /></Button>}
        </div>
      </div>

      <GuestCard g={m.guest} />

      <iframe title="mail" sandbox="" srcDoc={`<base target="_blank"><style>body{font-family:system-ui,sans-serif;font-size:14px;color:#111;margin:0;padding:4px;word-break:break-word}img{max-width:100%}</style>${doc}`}
        className="w-full min-h-[420px] rounded-xl border border-border bg-white" data-testid="mail-body-frame"
        onLoad={(e) => { try { e.target.style.height = `${Math.max(420, e.target.contentDocument.body.scrollHeight + 24)}px`; } catch { /* sandbox */ } }} />

      {m.attachments?.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="mail-attachments">
          {m.attachments.map((a) => (
            <a key={a.index} href={`${API}/api/mailbox/messages/${uid}/attachments/${a.index}?folder=${folder}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-secondary" data-testid={`mail-attachment-${a.index}`}>
              <Download className="h-3.5 w-3.5" />{a.filename} <span className="text-muted-foreground">({Math.ceil(a.size / 1024)} kB)</span>
            </a>
          ))}
        </div>
      )}

      {review && (
        <BookingReviewDialog
          data={review}
          guest={m.guest}
          message={m}
          onCancel={() => setReview(null)}
          onAskInfo={askInfo}
          onCreate={createFromReview}
        />
      )}
    </div>
  );
}
